import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, ShieldOff, Palette } from "lucide-react";
import { useSubscription } from "@/lib/subscription";
import { Link } from "react-router-dom";

export default function SettingsPage() {
  const { user } = useAuth();
  const sub = useSubscription();
  const [profile, setProfile] = useState({
    full_name: user?.full_name || "",
    company: user?.company || "",
    brand_color: user?.brand_color || "#d4a574",
    logo_url: user?.logo_url || "",
    app_title_override: user?.app_title_override || "",
    email_alerts: user?.email_alerts ?? true,
    sms_alerts: user?.sms_alerts ?? false,
    phone_number: user?.phone_number || "",
  });
  const [totp, setTotp] = useState({ enabled: user?.totp_enabled || false, secret: "", qr: "", code: "" });

  const saveProfile = async () => {
    try {
      await api.patch("/auth/profile", profile);
      localStorage.setItem("co_user", JSON.stringify({ ...user, ...profile }));
      toast.success("Profile saved. Reload to see full branding.");
    } catch { toast.error("Save failed"); }
  };

  const startTotp = async () => {
    try {
      const r = await api.post("/auth/2fa/setup");
      setTotp({ ...totp, secret: r.data.secret, qr: r.data.qr_data_url, code: "" });
    } catch { toast.error("2FA setup failed"); }
  };

  const verifyTotp = async () => {
    try {
      await api.post("/auth/2fa/verify", { code: totp.code });
      setTotp({ ...totp, enabled: true, qr: "", secret: "", code: "" });
      toast.success("2FA enabled — you'll be asked for a code at next sign-in.");
    } catch { toast.error("Invalid code. Try again."); }
  };

  const disableTotp = async () => {
    if (!window.confirm("Disable two-factor authentication?")) return;
    await api.post("/auth/2fa/disable");
    setTotp({ enabled: false, secret: "", qr: "", code: "" });
    toast.success("2FA disabled");
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Settings</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Preferences, Branding & Security</h1>
      </div>

      {/* Profile */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4">
        <div className="font-display text-lg font-semibold">Profile</div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Full name</Label><Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="pref-name" className="bg-[#241d16] border-[#33291f] mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Email</Label><Input value={user?.email || ""} readOnly className="bg-[#241d16] border-[#33291f] mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Company</Label><Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="pref-company" className="bg-[#241d16] border-[#33291f] mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Role</Label><Input value={user?.role || ""} readOnly className="bg-[#241d16] border-[#33291f] mt-1" /></div>
        </div>
      </div>

      {/* Branding */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4">
        <div className="font-display text-lg font-semibold flex items-center gap-2"><Palette size={16} className="text-[#d4a574]" /> White-Label Branding</div>
        <p className="text-[12px] text-[#b8ac96]">Customize the app for your team. Changes apply only to your view.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Accent color</Label>
            <div className="flex gap-2 mt-1">
              <Input type="color" value={profile.brand_color} onChange={(e) => setProfile({ ...profile, brand_color: e.target.value })} data-testid="brand-color" className="bg-[#241d16] border-[#33291f] w-16 h-10 p-1" />
              <Input value={profile.brand_color} onChange={(e) => setProfile({ ...profile, brand_color: e.target.value })} className="bg-[#241d16] border-[#33291f] font-mono flex-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">App title override</Label>
            <Input value={profile.app_title_override} onChange={(e) => setProfile({ ...profile, app_title_override: e.target.value })} placeholder="e.g. Meridian Distress Intel" data-testid="brand-title" className="bg-[#241d16] border-[#33291f] mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Logo URL</Label>
            <Input value={profile.logo_url} onChange={(e) => setProfile({ ...profile, logo_url: e.target.value })} placeholder="https://…" data-testid="brand-logo" className="bg-[#241d16] border-[#33291f] mt-1" />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4">
        <div className="font-display text-lg font-semibold">Notifications</div>
        <div className="flex items-center justify-between py-2">
          <div>
            <div className="text-[13px]">Email alerts</div>
            <div className="text-[11px] text-[#7a6d5a]">Saved searches, digests, new opportunities</div>
          </div>
          <Switch checked={profile.email_alerts} onCheckedChange={(v) => setProfile({ ...profile, email_alerts: v })} data-testid="pref-email-alerts" />
        </div>
        <div className="flex items-center justify-between py-2 border-t border-[#33291f] pt-4">
          <div>
            <div className="text-[13px] flex items-center gap-2">SMS auction alerts <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#d4a574]/10 text-[#d4a574] border border-[#d4a574]/30">Enterprise</span></div>
            <div className="text-[11px] text-[#7a6d5a]">Time-sensitive alerts for auctions closing within 24 hours</div>
          </div>
          <Switch checked={profile.sms_alerts} onCheckedChange={(v) => setProfile({ ...profile, sms_alerts: v })} data-testid="pref-sms-alerts" />
        </div>
        {profile.sms_alerts && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Mobile phone (E.164 format, e.g. +15551234567)</Label>
            <Input
              value={profile.phone_number}
              onChange={(e) => setProfile({ ...profile, phone_number: e.target.value })}
              placeholder="+15551234567"
              data-testid="pref-phone"
              className="bg-[#241d16] border-[#33291f] mt-1 font-mono"
            />
            <div className="text-[10px] text-[#7a6d5a] mt-1 font-mono">We only text you about auctions in your saved-search areas. Msg & data rates may apply.</div>
          </div>
        )}
      </div>

      <button onClick={saveProfile} data-testid="save-profile-btn" className="px-5 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988]">Save Preferences</button>

      {/* Billing */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4" data-testid="billing-section">
        <div className="font-display text-lg font-semibold flex items-center gap-2"><CreditCard size={16} className="text-[#d4a574]" /> Subscription & Billing</div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Current Plan</div>
            <div className="font-display text-2xl font-bold text-[#d4a574] uppercase">{sub.tier || "free"}</div>
            <div className="text-[11px] text-[#7a6d5a] font-mono">{sub.status || "inactive"}</div>
          </div>
          <div className="flex gap-2">
            <Link to="/pricing" data-testid="billing-view-plans" className="px-4 py-2 rounded border border-[#4a3a2b] text-[13px] text-[#d4a574] hover:border-[#d4a574]">{sub.tier === "free" ? "Upgrade" : "Change Plan"}</Link>
            {sub.stripe_customer_id && (
              <button onClick={async () => {
                const r = await api.post("/payments/portal", { origin_url: window.location.origin });
                window.location.href = r.data.url;
              }} data-testid="billing-portal-btn" className="px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px]">Manage Billing →</button>
            )}
          </div>
        </div>
      </div>

      {/* 2FA */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-6 space-y-4">
        <div className="font-display text-lg font-semibold flex items-center gap-2">
          {totp.enabled ? <ShieldCheck size={16} className="text-[#34d399]" /> : <ShieldOff size={16} className="text-[#7a6d5a]" />}
          Two-Factor Authentication
        </div>
        {totp.enabled ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-[13px] text-[#34d399]">✓ 2FA is enabled on your account.</div>
            <button onClick={disableTotp} data-testid="totp-disable" className="px-4 py-2 rounded border border-[#f87171]/50 text-[#f87171] text-[12px] hover:bg-[#f87171]/10">Disable 2FA</button>
          </div>
        ) : totp.qr ? (
          <div className="space-y-3">
            <div className="text-[12px] text-[#b8ac96]">Scan this QR with Google Authenticator / Authy / 1Password, then enter the 6-digit code.</div>
            <img src={totp.qr} alt="TOTP QR" className="w-40 h-40 rounded border border-[#33291f] bg-white p-2" />
            <div className="font-mono text-[11px] text-[#7a6d5a]">Secret: {totp.secret}</div>
            <div className="flex gap-2 max-w-xs">
              <Input value={totp.code} onChange={(e) => setTotp({ ...totp, code: e.target.value })} maxLength={6} placeholder="123456" data-testid="totp-code" className="bg-[#241d16] border-[#33291f] font-mono text-center tracking-widest" />
              <button onClick={verifyTotp} data-testid="totp-verify" className="px-4 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988]">Verify</button>
            </div>
          </div>
        ) : (
          <button onClick={startTotp} data-testid="totp-enable" className="px-4 py-2 rounded border border-[#4a3a2b] text-[#d4a574] text-[13px] hover:border-[#d4a574]">Enable 2FA</button>
        )}
      </div>
    </div>
  );
}
